import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Tracks whether the manager's restaurant has finished the welcome
// flow yet. Fetches a single row's onboarded flag — kept tiny so the
// Daily Huddle can mount the welcome card without pulling all of
// useSettingsData (which is a heavier hook owning items + suppliers
// + sales summary + etc).
//
// Two mutators:
//   markOnboarded()         — flips onboarded=true (used when the
//                             manager dismisses the welcome card OR
//                             completes the wizard)
//   updateRestaurantBasics({ name, daily_target, large_entry_threshold,
//                            timezone })
//                           — persists the wizard's step-1/step-2
//                             inputs to the restaurants row + mirrors
//                             into settings (legacy duplication).
export function useOnboardingStatus() {
  const [restaurant, setRestaurant] = useState(null)
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // RLS scopes to the user's restaurant. Pull the onboarding-
      // relevant columns only.
      const { data, error: fetchErr } = await supabase
        .from('restaurants')
        .select('id, name, timezone, daily_target, large_entry_threshold, onboarded, plan, trial_ends_at')
        .eq('active', true)
        .order('created_at')
        .limit(1)
        .maybeSingle()
      if (fetchErr) throw fetchErr
      setRestaurant(data ?? null)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useOnboardingStatus]', e)
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchStatus() }, [fetchStatus])

  const markOnboarded = useCallback(async () => {
    if (!restaurant?.id) return
    const { error: updErr } = await supabase
      .from('restaurants')
      .update({ onboarded: true, updated_at: new Date().toISOString() })
      .eq('id', restaurant.id)
    if (updErr) throw updErr
    await fetchStatus()
  }, [restaurant, fetchStatus])

  const updateRestaurantBasics = useCallback(async (patch) => {
    if (!restaurant?.id) return
    const updates = {
      ...patch,
      updated_at: new Date().toISOString(),
    }
    const { error: updErr } = await supabase
      .from('restaurants')
      .update(updates)
      .eq('id', restaurant.id)
    if (updErr) throw updErr

    // Mirror the daily_target + large_entry_threshold into the legacy
    // settings table (some hooks still read it from there).
    const settingsPatch = {}
    if (patch.daily_target          != null) settingsPatch.daily_target = patch.daily_target
    if (patch.large_entry_threshold != null) settingsPatch.large_entry_threshold = patch.large_entry_threshold
    if (Object.keys(settingsPatch).length > 0) {
      settingsPatch.updated_at = new Date().toISOString()
      await supabase.from('settings')
        .update(settingsPatch)
        .eq('restaurant_id', restaurant.id)
    }
    await fetchStatus()
  }, [restaurant, fetchStatus])

  return {
    restaurant,
    loading,
    error,
    refresh: fetchStatus,
    markOnboarded,
    updateRestaurantBasics,
  }
}
